import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { and } from 'ember-truth-helpers'
import { formatNumber, t, type IntlService } from 'ember-intl'
import { type Task } from 'ember-concurrency'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { Panel, PanelHeader, PanelSubHeader, PanelTitle, PanelBody } from 'district-ui-client/components/section'
import { joinList } from 'district-ui-client/helpers/join-list'
import { ThemedButton } from 'district-ui-client/components/themed-button'
import { TooltipInfo } from 'district-ui-client/components/tooltip'
import { formatValue } from 'district-ui-client/utils/format-value'
import { TableContainer, Table, TBody, TH, TD, TFootSingleTd } from 'district-ui-client/components/table'
import { includes } from 'district-ui-client/helpers/includes'
import type ReportingService from 'district-ui-client/services/reporting'
import {
  type BaseReport,
  type BaseReportMetaPaging,
  type BaseReportMetaStandardsSet,
} from 'district-ui-client/services/reporting'
import { service } from '@ember/service'
import {
  avgScoreColor,
  StudentCountDistribution,
  type DistributionBands,
} from 'district-ui-client/components/reporting/standards-performance/results'
import type ReportNavigatorService from 'district-ui-client/services/report-navigator'
import { PageControls } from 'district-ui-client/components/base/page-controls'
import { posthogEvent } from 'district-ui-client/modifiers/posthog'
import { Product } from 'district-ui-client/domain/product'

interface PerformanceItem {
  total_students: number
  average_score: Nullable<number>
  student_count_distribution: Nullable<DistributionBands>
}

interface Signature {
  Args: {
    reportData: StandardsPerformanceResultsMxReportData
    isReloading?: boolean
    exportCsv?: Task<unknown, unknown[]>
    isSingleSchool?: boolean
  }
}

export interface StandardsPerformanceResultsMxReportData extends BaseReport {
  aggregate: {
    total_schools: number
    total_students: number
  }
  results: {
    id: string // unique identifier for the result item, used for toggling rows
    content_level: string
    lesson_level_domain_code: string // Mx grade index & domain code, e.g. '0.MD'
    grade_name_domain_code: string // RE short grade name & domain code, e.g. 'K.MD'
    lesson_concepts: string[] // ['lesson number: name', 'PA, Phonics: letter sound, intitial sounds', ...]
    precincts: string[] // activity types are stored in the db as precinct
    standards: string[]
    aggregate: PerformanceItem
    schools: ({ school_id: string; school_name: string } & PerformanceItem)[]
  }[]
  meta: BaseReportMetaPaging & BaseReportMetaStandardsSet
}

export class StandardsPerformanceResultsMx extends Component<Signature> {
  @service intl!: IntlService
  @service reporting!: ReportingService
  @service reportNavigator!: ReportNavigatorService

  @tracked openedRows: string[] = []

  get allOpened() {
    return this.openedRows.length === this.args.reportData.results.length
  }

  toggleRow = (rowId: string) => {
    if (this.openedRows.includes(rowId)) {
      this.openedRows = this.openedRows.filter((id) => rowId !== id)
    } else {
      this.openedRows = [...this.openedRows, rowId]
    }
  }

  toggleAll = () => {
    if (this.allOpened) {
      this.openedRows = []
    } else {
      this.openedRows = this.args.reportData.results.map((resultItem) => resultItem.id) ?? []
    }
  }

  // this needs to be based on scope selection, rather than number of schools in reporting results data
  get showExpander() {
    return !this.args.isSingleSchool
  }

  <template>
    <Panel data-test-standards-performance-results-panel>
      <PanelHeader>
        <div class="flex items-start justify-between gap-4">
          <div class="space-y-2">
            <PanelTitle>{{t "reporting.standardsPerformance.standardsResults"}}</PanelTitle>
            <div class="text-sm">
              {{t
                "reporting.showingDataForStudentsFromSchools"
                studentCount=(formatNumber @reportData.aggregate.total_students)
                schoolCount=(formatNumber @reportData.aggregate.total_schools)
              }}
            </div>
          </div>
          {{#if @exportCsv}}
            <ThemedButton
              class="print:hidden"
              @style="theme"
              @disabled={{@exportCsv.isRunning}}
              {{on "click" @exportCsv.perform}}
              {{posthogEvent "export-report-csv" report="standards-performance" product=Product.MX}}
            >
              {{t "exportCsv"}}
            </ThemedButton>
          {{/if}}
        </div>
      </PanelHeader>
      <PanelSubHeader>
        <div class="flex gap-5 text-sm font-medium">
          <div class="flex items-center gap-2">
            {{! template-lint-disable no-bare-strings }}
            <PhosphorIcon @icon="phosphor-circle-fill" class="text-red-200" /><span
              class="text-neutral-300"
            >0-49%</span>
          </div>
          <div class="flex items-center gap-2">
            {{! template-lint-disable no-bare-strings }}
            <PhosphorIcon @icon="phosphor-circle-fill" class="text-yellow-200" /><span
              class="text-neutral-300"
            >50-79%</span>
          </div>
          <div class="flex items-center gap-2">
            {{! template-lint-disable no-bare-strings }}
            <PhosphorIcon @icon="phosphor-circle-fill" class="text-green-200" /><span
              class="text-neutral-300"
            >80-100%</span>
          </div>
        </div>
      </PanelSubHeader>
      <PanelBody @tight={{true}}>
        <TableContainer>
          <Table @minWidth="min-w-[60rem]">
            <thead>
              <tr class="bg-neutral-50">
                {{#if this.showExpander}}
                  <TH class="w-16 text-center">
                    <button data-test-expand-all type="button" class="text-base" {{on "click" this.toggleAll}}>
                      {{#if this.allOpened}}
                        <PhosphorIcon @icon="phosphor-caret-up-bold" class="h-[20px] w-[20px] text-blue-325" />
                      {{else}}
                        <PhosphorIcon @icon="phosphor-caret-down-bold" class="h-[20px] w-[20px]" />
                      {{/if}}
                    </button>
                  </TH>
                {{/if}}
                {{! avoids applying divide classes to the expander cell }}
                <div class="contents divide-x-2 divide-white align-middle">
                  <TH @sortKey="lesson_level_domain_code" class="w-24">
                    {{t "reporting.standardsPerformance.headers.gradeDomain" htmlSafe=true}}
                  </TH>
                  <TH class="w-28">{{t "reporting.standardsPerformance.headers.standardsMx"}}</TH>
                  <TH>{{t "reporting.standardsPerformance.headers.lessonFocusMx"}}</TH>
                  <TH class="w-32">
                    {{t "reporting.standardsPerformance.headers.numberOfStudents" htmlSafe=true}}
                  </TH>
                  <TH class="w-52">
                    {{t "reporting.standardsPerformance.headers.studentCountDistribution" htmlSafe=true}}
                    <TooltipInfo @text={{t "reporting.standardsPerformance.headers.studentCountDistributionTooltip"}} />
                  </TH>
                  <TH @sortKey="average_score" class="w-32">
                    {{t "reporting.standardsPerformance.headers.averageScore" htmlSafe=true}}
                    <TooltipInfo @text={{t "reporting.standardsPerformance.headers.averageScoreTooltipMx"}} />

                  </TH>
                </div>
              </tr>
            </thead>
            <TBody class={{if @isReloading "text-neutral-100"}}>
              {{#each @reportData.results as |resultItem|}}
                {{#let (and this.showExpander (includes this.openedRows resultItem.id)) as |isOpenedRow|}}
                  <tr class={{if isOpenedRow "bg-neutral-50 font-semibold" "bg-white hover:bg-blue-50"}}>
                    {{#if this.showExpander}}
                      <TD class="text-center">
                        <button
                          data-test-expand
                          type="button"
                          class="text-base"
                          {{on "click" (fn this.toggleRow resultItem.id)}}
                        >
                          {{#if isOpenedRow}}
                            <PhosphorIcon @icon="phosphor-caret-up-bold" class="h-[20px] w-[20px] text-blue-325" />
                          {{else}}
                            <PhosphorIcon @icon="phosphor-caret-down-bold" class="h-[20px] w-[20px]" />
                          {{/if}}
                        </button>
                      </TD>
                    {{/if}}
                    <TD>{{resultItem.grade_name_domain_code}}</TD>
                    <TD>{{joinList resultItem.standards ", "}}</TD>
                    <TD class="min-w-32">
                      {{#each resultItem.lesson_concepts as |concept|}}
                        <div>{{concept}}</div>
                      {{/each}}
                    </TD>
                    <TD class="font-semibold">
                      <span class="text-lg"><PhosphorIcon @icon="phosphor-users-three-fill" class="mr-2" /></span>
                      {{formatNumber resultItem.aggregate.total_students}}
                    </TD>
                    <TD>
                      <StudentCountDistribution @distribution={{resultItem.aggregate.student_count_distribution}} />
                    </TD>
                    <TD class="font-semibold" @textColor={{avgScoreColor resultItem.aggregate.average_score}}>
                      {{formatValue resultItem.aggregate.average_score append="%"}}
                    </TD>
                  </tr>
                  {{#if isOpenedRow}}
                    {{#each resultItem.schools as |schoolDistributionItem|}}
                      <tr class="bg-neutral-50 font-semibold">
                        <TD />
                        <TD colspan="3">{{schoolDistributionItem.school_name}}</TD>
                        <TD>
                          <span class="text-lg"><PhosphorIcon @icon="phosphor-users-three-fill" class="mr-2" /></span>
                          {{formatNumber schoolDistributionItem.total_students}}
                        </TD>
                        <TD>
                          <StudentCountDistribution
                            @distribution={{schoolDistributionItem.student_count_distribution}}
                          />
                        </TD>
                        <TD @textColor={{avgScoreColor schoolDistributionItem.average_score}}>
                          {{formatValue schoolDistributionItem.average_score append="%"}}
                        </TD>
                      </tr>
                    {{/each}}
                  {{/if}}
                {{/let}}
              {{/each}}
            </TBody>
            <TFootSingleTd>
              <PageControls @meta={{@reportData.meta}} />
            </TFootSingleTd>
          </Table>
        </TableContainer>
      </PanelBody>
    </Panel>
  </template>
}
